import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    Output,
    ViewChild,
    EventEmitter,
    ViewEncapsulation,
    TemplateRef,
    SimpleChanges,
    Injector,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors } from '@angular/forms';
import { isDefined, isEqual } from '@fi-shared/utils';
import { BehaviorSubject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { map } from 'rxjs/operators';
import { MatOptionSelectionChange } from '@angular/material/core';
import {
    FiDropdownActionGuard,
    FiDropdownActionResult,
    FiDropdownItemAdditionalTextFn,
    FiDropdownItemDisabledFn,
    FiDropdownItemTextClassFn,
    FiDropdownItemTitleFn,
    IFiDropdownEventData,
} from '@fi-shared/fi-select-search/fi-select-search';
import { SelectSearchBase } from '@fi-shared/fi-select-search/select-search-base';
import { AppTextService } from '@fi-shared/app-text';
import { MatFormFieldAppearance } from '@angular/material/form-field';

@Component({
    selector: 'fi-select-search',
    templateUrl: './select-search.component.html',
    styleUrls: ['./select-search.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiSelectSearchComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiSelectSearchComponent,
            multi: true,
        },
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-select-search' },
    standalone: false,
})
export class FiSelectSearchComponent<T, TRes = number> extends SelectSearchBase<T, TRes | TRes[]> implements OnInit {
    @Input() items: T[] = [];
    @Input() selectSuffixIconClassName: string; // todo: remove
    @Input() label: string = '';
    @Input() hintText: string;
    @Input() displayValue: string = 'Name';
    @Input() isTextFieldLabelType: boolean;
    @Input() matOptionValueProperty: string = 'Id';
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() isMultiSelect: boolean = false;
    @Input() showDeleteItemButton: boolean = false;
    @Input() showEditItemButton: boolean = false;
    @Input() errorText: string = this.appTextService.get('error_message.required_dropdown');
    @Input() filterBy: string | string[] = 'Name';
    @Input() isSelectAllShown: boolean = false;
    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() includeDisabledToSearchResult = true;
    @Input() panelClass: string = '';
    @Input() isResetOptionForceShown: boolean;
    @Input() isFormFieldNewDefaultStyle: boolean; // todo: remove
    @Input() isPrefixSearchIconShown: boolean = false;
    @Input() isFormFieldRolloutErrorMessage: boolean = false;
    @Input() isFormFieldRelativeSubscript: boolean = false;
    @Input() hideRequiredMarker: boolean = false;
    // todo use trackByFn instead of matOptionValueProperty
    @Input() trackByFn: (item: T) => TRes = (item) => item[this.matOptionValueProperty];

    // resolve promise with 'result: false' for cancel selecting item
    @Input() canSelectItemFn: FiDropdownActionGuard<T> = () => Promise.resolve({ result: true });

    // resolve promise with 'result: false' for cancel deleting item
    @Input() canRemoveItemFn: FiDropdownActionGuard<T> = () => Promise.resolve({ result: true });

    // get option title attribute in dropdown
    @Input() getOptionTitleFn: FiDropdownItemTitleFn<T> = () => undefined;

    @Input() getOptionTextClass: FiDropdownItemTextClassFn<T> = () => undefined;

    @Input() getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<T> = () => undefined;
    @Input() getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<T> = () => undefined;

    @Input() customMatOptionNameTemplate: TemplateRef<T>;
    @Input() isCustomMatSelectTrigger: boolean = false;

    // determine if item in hidden from dropdown
    @Input() isOptionHiddenFn: FiDropdownItemDisabledFn<T>;

    @ViewChild('matSelectModel', { static: true }) matSelectModel: NgModel;
    @ViewChild(CdkVirtualScrollViewport) virtualScrollViewportComponent: CdkVirtualScrollViewport;
    @Output() onDeleteButtonClick = new EventEmitter<T>();
    @Output() onEditButtonClick = new EventEmitter<T>();
    @Output() selectedItemChanged = new EventEmitter<IFiDropdownEventData<T>>();
    @Output() onSelectAllClick = new EventEmitter<any | any[]>();
    get emptyFilteredItems$() {
        return this.filteredItems$.pipe(map((v) => !v.length));
    }

    @Input() itemSize: number = 42;
    readonly minBufferPx: number = 420;
    readonly maxBufferPx: number = 840;
    readonly maxItemsToShow: number = 5;
    readonly listItemBasePadding = 16;

    private readonly deselectAllButtonLabel: string = 'common.deselect_all';
    private readonly selectAllButtonLabel: string = 'select_all';

    get isResetOptionShown(): boolean {
        if (this.isResetOptionForceShown) return true;

        return !this.isMultiSelect && !this.required;
    }

    selectedItems$ = new BehaviorSubject<T[]>([]);

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
    ) {
        super(injector);
    }

    isAllSelected: boolean = false;

    ngOnInit() {
        super.ngOnInit();

        // https://chainformation.atlassian.net/browse/FI-4659
        // we should render selected items, otherwise we can not see them in input
        this.updateSelectedItemsFromModel();
        this.updateFilteredItems();
        this.setIsAllSelected();
    }

    protected getPanelClass() {
        const classes = ['fi-select-search-panel'];
        if (this.panelClass) {
            classes.push(this.panelClass);
        }
        if (this.isSelectAllShown) {
            classes.push('is-select-all-shown');
        }
        return classes.join(' ');
    }

    getMatOptionClass(item: T): { [key: string]: boolean } {
        return {
            'mat-select-option-text-space-between':
                this.isMatOptionActionsShown(item) || !!this.getOptionAdditionalText(item),
        };
    }

    getViewportHeight(): number {
        let itemsLength: number = this.filteredItems$.value.length;
        if (this.isResetOptionShown) {
            itemsLength++;
        }
        return itemsLength < this.maxItemsToShow ? itemsLength * this.itemSize : this.maxItemsToShow * this.itemSize;
    }

    onItemSelected(item: T, $event: MatOptionSelectionChange) {
        if (!$event.isUserInput) return;

        const previousSelection: T[] = this.getSelectedItemsFromModel();

        let isSelected: boolean = $event.source.selected;
        if (!isDefined(item)) {
            // reset option clicked
            item = this.selectedItems$.value[0];
            isSelected = false;
        }

        this.canSelectItemFn({ item, isSelected }).then((dropdownActionResult: FiDropdownActionResult) => {
            if (dropdownActionResult.result) {
                this.selectedItemChanged.emit({ item, isSelected });
            } else {
                // restore previous value
                this.selectedItems$.next(previousSelection);
                this.doPropagateChanges();
            }
            this.setIsAllSelected();
        });
    }

    onSelectOpenedChanged($event: boolean, element: HTMLInputElement) {
        if ($event) {
            // by some reason sometimes we have empty lines in viewport, so should rerender content
            this.virtualScrollViewportComponent.checkViewportSize();
            element.focus();
        } else {
            if (!this.filteredItems$.value.length) {
                this.searchText = '';
                this.onSearchTextChanged();
            }
        }
    }

    private currentHoveredItem: T;
    setCurrentHoveredItem(item: T = undefined) {
        this.currentHoveredItem = isDefined(item) ? Object.assign(item as Object) : item;
    }

    isMatOptionActionsShown(item: T): boolean {
        return isEqual(this.currentHoveredItem, item) && (this.showDeleteItemButton || this.showEditItemButton);
    }

    deleteItem(item: T, $event): void {
        $event.stopPropagation();
        this.onDeleteButtonClick.emit(item);
    }

    editItem(item: T, $event): void {
        $event.stopPropagation();
        this.onEditButtonClick.emit(item);
    }

    getOptionTitle(item: T): string {
        return this.getOptionTitleFn(item, this.items);
    }

    getOptionAdditionalText(item: T): string {
        return this.getOptionAdditionalTextFn(item, this.items);
    }

    getOptionAdditionalClass(item: T): string {
        return this.getOptionAdditionalClassFn(item, this.items);
    }

    onSelectionChanged() {
        this.updateSelectedItemsFromModel();
        this.doPropagateChanges();
        this.setIsAllSelected();
    }

    protected updateFilteredItems() {
        const filteredItems: T[] =
            this.searchText || isDefined(this.isOptionHiddenFn) ? this.getFilteredItems() : this.items;
        this.filteredItems$.next(filteredItems);
    }

    private getFilteredItems(): T[] {
        return this.items.filter((dataItem: T) => {
            if (isDefined(this.isOptionHiddenFn) && this.isOptionHiddenFn(dataItem, this.items)) {
                return false;
            }

            if (!this.searchText) {
                return true;
            }

            for (let filterField of this.getDataItemFilteredFields(dataItem)) {
                if (!isDefined(dataItem[filterField]) || dataItem[filterField] === null) continue;

                if (
                    dataItem[filterField].toString().toLowerCase().indexOf(this.searchText.toLowerCase()) > -1 &&
                    (this.includeDisabledToSearchResult || !this.searchText || !this.isOptionDisabled(dataItem))
                )
                    return true;
            }
        });
    }

    private getDataItemFilteredFields(dataItem: T): string[] {
        if (!this.filterBy) return Object.keys(dataItem);

        if (Array.isArray(this.filterBy)) return this.filterBy;

        return [this.filterBy];
    }

    protected getSelectedItemsFromModel() {
        return this.items.filter((item: T) => {
            if (this.ngModel instanceof Array) {
                return this.ngModel.findIndex((modelItem) => modelItem === this.getItemValue(item)) !== -1;
            }
            return this.ngModel === this.getItemValue(item);
        });
    }

    toggleAllSelection() {
        this.isAllSelected = !this.isAllSelected;
        this.ngModel = !this.isAllSelected ? [] : this.getAllElementsValues();
        this.selectedItems$.next(this.getSelectedItemsFromModel());
        this.onSelectAllClick.emit(this.ngModel);
    }

    getSelectAllText(): string {
        return this.isAllSelected ? this.deselectAllButtonLabel : this.selectAllButtonLabel;
    }

    private setIsAllSelected() {
        if (this.isSelectAllShown && Array.isArray(this.ngModel))
            this.isAllSelected = this.ngModel.length >= this.items.length;
    }

    private getAllElementsValues(): TRes[] {
        return this.items.map((item) => this.getItemValue(item));
    }

    doPropagateChanges() {
        this.ngModel = this.getNgModelDataBySelectedItems();
        super.doPropagateChanges();
    }

    doValidate(): ValidationErrors {
        const validationResult = super.doValidate();
        setTimeout(() => {
            // result like { required: false } is treated at error by angular components
            // so have to pass null if all properties are false
            const coercedValidationResult = this.coerceValidationResult(validationResult);
            this.matSelectModel.control.setErrors(coercedValidationResult);
        });
        return validationResult;
    }

    private getNgModelDataBySelectedItems(): TRes | TRes[] {
        const selectedItems: T[] = this.selectedItems$.value;
        return this.isMultiSelect
            ? selectedItems.map((item) => this.getItemValue(item))
            : selectedItems.length
              ? this.getItemValue(selectedItems[0])
              : undefined;
    }

    getItemValue(item: T) {
        return this.trackByFn(item);
    }

    getItemDisplayValue(item: T) {
        return this.isTextFieldLabelType ? this.appTextService.get(item[this.displayValue]) : item[this.displayValue];
    }
}
